import { ReactNode, useEffect, useState } from "react";
import { AdminSidebar } from "./AdminSidebar";
import { Button } from "@jambushrushti/components/ui/button";
import { Menu, Bell, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

interface AdminLayoutProps {
  children: ReactNode;
  title?: string;
}

export function AdminLayout({ children, title }: AdminLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Dialogs, menus and toasts render in portals outside this tree; this class gives them the
  // panel's heritage theme (site/styles/admin-theme.css) while the panel is open.
  useEffect(() => {
    document.documentElement.classList.add("jbs-cms-admin");
    return () => document.documentElement.classList.remove("jbs-cms-admin");
  }, []);

  return (
    <div className="cms-admin-module jb-admin-shell flex min-h-screen text-umber-900">
      {/* Desktop Sidebar */}
      <div className="hidden lg:block">
        <AdminSidebar />
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-umber-950/60 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="animate-fade-in absolute left-0 top-0 h-full w-72">
            <AdminSidebar onClose={() => setSidebarOpen(false)} />
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex min-h-screen w-full min-w-0 flex-1 flex-col">
        {/* Top Header */}
        <header className="jb-admin-header sticky top-0 z-40 border-b border-border bg-[#fffaf3]/90 backdrop-blur-xl">
          <div className="flex h-14 items-center justify-between px-4 sm:h-[68px] sm:px-6 lg:px-8">
            <div className="flex min-w-0 items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-lg text-umber-700 hover:bg-heritage-100/60 lg:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="h-5 w-5" />
              </Button>
              {title && (
                <h1 className="truncate text-lg font-semibold text-heritage-800 sm:text-[22px] md:text-[26px]">
                  {title}
                </h1>
              )}
            </div>

            <div className="flex shrink-0 items-center gap-2 sm:gap-3">
              <Link
                to="/"
                target="_blank"
                rel="noreferrer"
                className="hidden items-center gap-1.5 rounded-md border border-[#8a4a2a]/40 bg-[#fffbf5]/70 px-3 py-1.5 text-[13px] font-medium text-heritage-800 transition-colors hover:bg-[#fffaf3] hover:shadow-sm md:inline-flex"
              >
                View website
                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              </Link>
              <Link
                to="/"
                target="_blank"
                rel="noreferrer"
                title="View Public Website"
                className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-[#8a4a2a]/40 bg-[#fffbf5]/70 text-heritage-800 md:hidden"
              >
                <ExternalLink className="h-4 w-4" aria-hidden="true" />
              </Link>

              {/* Notifications */}
              <Button
                variant="outline"
                size="icon"
                className="relative h-9 w-9 rounded-md border-[#8a4a2a]/40 bg-[#fffbf5]/70 text-heritage-800 hover:bg-[#fffaf3] hover:text-heritage-800"
              >
                <Bell className="h-4 w-4" />
                <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-[#ca9f36] ring-2 ring-[#fffaf3]" />
              </Button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto px-2.5 py-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
